import { MAX_CHAT_CHARS, type RoomChatEntry } from "@golazo/shared";
import type { OnlineClient } from "./client.js";

/**
 * El chat del lobby: la gente que esta mirando la lista de salas. Se entra al
 * abrir el lobby y se sale al cerrarlo, para que el servidor no se lo mande a
 * quien esta jugando.
 */
export interface LobbyChat {
  entries(): readonly RoomChatEntry[];
  /** Cuantos estan mirando el lobby ahora mismo. */
  here(): number;
  subscribe(fn: (entries: readonly RoomChatEntry[], added: readonly RoomChatEntry[]) => void): () => void;
  enter(): void;
  leave(): void;
  send(text: string): void;
  dispose(): void;
}

const KEEP = 100;

export function createLobbyChat(client: OnlineClient): LobbyChat {
  let lines: RoomChatEntry[] = [];
  let here = 0;
  let inside = false;
  const listeners = new Set<(entries: readonly RoomChatEntry[], added: readonly RoomChatEntry[]) => void>();

  const unsubMsg = client.onMessage((msg) => {
    if (msg.type !== "lobby:chat") return;
    here = msg.here;
    if (msg.snapshot) {
      // La foto al entrar SUSTITUYE lo que hubiera: tras un reinicio del
      // servidor los ids de antes no significan nada.
      lines = msg.entries.slice(-KEEP);
      for (const fn of listeners) fn(lines, []);
      return;
    }
    const known = new Set(lines.map((l) => l.id));
    const added = msg.entries.filter((e) => !known.has(e.id));
    if (added.length > 0) lines = [...lines, ...added].sort((a, b) => a.id - b.id).slice(-KEEP);
    for (const fn of listeners) fn(lines, added);
  });

  // Tras una reconexion el servidor ya no le tiene en el lobby: se vuelve a entrar.
  const unsubState = client.onState((state) => {
    if (state === "online" && inside) client.send({ type: "lobby:enter" });
  });

  return {
    entries: () => lines,
    here: () => here,
    subscribe(fn) {
      listeners.add(fn);
      fn(lines, []);
      return () => listeners.delete(fn);
    },
    enter() {
      inside = true;
      client.send({ type: "lobby:enter" });
    },
    leave() {
      if (!inside) return;
      inside = false;
      client.send({ type: "lobby:leave" });
    },
    send(text) {
      const clean = text.replace(/\s+/g, " ").trim().slice(0, MAX_CHAT_CHARS);
      if (clean) client.send({ type: "lobby:chat", text: clean });
    },
    dispose() {
      unsubMsg();
      unsubState();
      listeners.clear();
    },
  };
}
